import { resolveAssignmentInstanceLabel } from '../../../utils/assignmentLabels';
import {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  fetchAssignmentGroupDescriptionsForCourse,
  fetchAssignmentGroupLabelsForCourse,
  renameStudentAssignmentGroup,
  fetchCoursePlacementLabels,
  getCourseGroupDescription,
  getCourseGroupDisplayName,
  getStreamDisplayName,
  renameStudentCourseGroup
} from '../../../utils/groupLabels';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function headers() {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY
  };
}

async function readJson(response, label, fallback = null) {
  const text = await response.text();

  if (!response.ok) {
    if (fallback !== null) return fallback;
    throw new Error(
      `${label} failed (${response.status}): ${text || response.statusText}`
    );
  }

  return text ? JSON.parse(text) : fallback;
}

function pairKey(assignmentId, groupNumber) {
  return `${assignmentId}|${groupNumber}`;
}

function naturalCompare(a, b) {
  return String(a || '').localeCompare(
    String(b || ''),
    undefined,
    { numeric: true, sensitivity: 'base' }
  );
}

function membershipSignature(rows) {
  return (rows || [])
    .map(row => String(row.enrolment_id || ''))
    .filter(Boolean)
    .sort(naturalCompare)
    .join('|');
}

function studentName(row) {
  const student = row?.enrolments?.students || {};
  return (
    student.english_name ||
    student.chinese_name ||
    student.student_code ||
    'Student'
  );
}

function groupContextsByIdentity(
  contexts
) {
  const rows =
    Array.isArray(contexts)
      ? contexts.filter(Boolean)
      : [];

  const parent =
    rows.map(
      (_, index) =>
        index
    );

  function find(index) {
    if (
      parent[index] !==
      index
    ) {
      parent[index] =
        find(
          parent[index]
        );
    }

    return parent[index];
  }

  function union(a, b) {
    const rootA =
      find(a);

    const rootB =
      find(b);

    if (
      rootA !== rootB
    ) {
      parent[rootB] =
        rootA;
    }
  }

  const byLogicalId =
    new Map();

  const bySignature =
    new Map();

  rows.forEach(
    (context, index) => {
      const logicalId =
        context?.logicalId
          ? String(
              context.logicalId
            )
          : '';

      if (logicalId) {
        if (
          byLogicalId.has(
            logicalId
          )
        ) {
          union(
            index,

            byLogicalId.get(
              logicalId
            )
          );
        } else {
          byLogicalId.set(
            logicalId,
            index
          );
        }
      }

      const signature =
        String(
          context?.signature ||
          ''
        );

      if (signature) {
        if (
          bySignature.has(
            signature
          )
        ) {
          union(
            index,

            bySignature.get(
              signature
            )
          );
        } else {
          bySignature.set(
            signature,
            index
          );
        }
      }
    }
  );

  const grouped =
    new Map();

  rows.forEach(
    (context, index) => {
      const root =
        find(index);

      if (
        !grouped.has(root)
      ) {
        grouped.set(
          root,
          []
        );
      }

      grouped
        .get(root)
        .push(context);
    }
  );

  return Array.from(
    grouped.values()
  );
}


async function loadCourseGroupContext(
  courseId,
  enrolment
) {
  const streamNumber =
    enrolment?.stream ??
    null;

  const groupNumber =
    enrolment
      ?.group_number ??
    null;

  if (
    !courseId ||
    streamNumber === null ||
    streamNumber === '' ||
    groupNumber === null ||
    groupNumber === ''
  ) {
    return null;
  }

  const [
    membersResponse,
    placementLabels
  ] =
    await Promise.all([
      fetch(
        `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${encodeURIComponent(courseId)}&stream=eq.${encodeURIComponent(streamNumber)}&group_number=eq.${encodeURIComponent(groupNumber)}&active=eq.true&select=id,students(student_code,english_name,chinese_name,is_master)`,
        {
          headers:
            headers()
        }
      ),

      fetchCoursePlacementLabels(
        courseId
      )
    ]);

  const rows =
    await readJson(
      membersResponse,
      'Loading course group members',
      []
    );

  const members =
    (rows || [])
      .filter(
        row =>
          row
            ?.students
            ?.is_master !==
          true
      )
      .map(
        row => ({
          enrolment_id:
            row.id,

          enrolments: {
            id:
              row.id,

            students:
              row.students ||
              {}
          }
        })
      )
      .sort(
        (a, b) =>
          naturalCompare(
            a
              ?.enrolments
              ?.students
              ?.student_code,

            b
              ?.enrolments
              ?.students
              ?.student_code
          )
      );

  const labelKey =
    `${streamNumber}|${groupNumber}`;

  return {
    source:
      'course',

    streamNumber,
    groupNumber,

    members,

    signature:
      membershipSignature(
        members
      ),

    logical:
      null,

    logicalId:
      null,

    customName:
      String(
        placementLabels
          ?.groups
          ?.[labelKey]
          ?.name ||
        ''
      ).trim(),

    name:
      getCourseGroupDisplayName(
        placementLabels,
        streamNumber,
        groupNumber
      ),

    description:
      getCourseGroupDescription(
        placementLabels,
        streamNumber,
        groupNumber
      ),

    streamName:
      getStreamDisplayName(
        placementLabels,
        streamNumber
      )
  };
}


function displayAssignmentName(assignment, setting) {
  return (
    String(setting?.display_name || '').trim() ||
    assignment?.assignment_types?.name ||
    'Assignment'
  );
}

function buildRelevantTo({
  contexts,
  allStudentContexts,
  assignments,
  settingByType,
  sequenceById
}) {
  const contextByAssignment = new Map(
    allStudentContexts.map(context => [
      String(context.assignment.id),
      context
    ])
  );

  const configuredGroupAssignmentIds = new Set(
    allStudentContexts.map(context =>
      String(context.assignment.id)
    )
  );

  const bySequence = new Map();
  const nonSequence = [];

  contexts.forEach(context => {
    const sequenceId = context.sequenceId || null;

    if (!sequenceId) {
      nonSequence.push(context);
      return;
    }

    if (!bySequence.has(String(sequenceId))) {
      bySequence.set(String(sequenceId), []);
    }

    bySequence.get(String(sequenceId)).push(context);
  });

  const results = [];
  const consumed = new Set();

  for (const [sequenceId, sequenceContexts] of bySequence.entries()) {
    const expected = assignments.filter(assignment => {
      const setting = settingByType.get(
        String(assignment.assignment_type_id)
      );

      return (
        String(setting?.sequence_id || '') === sequenceId &&
        assignment.groups_enabled === true &&
        configuredGroupAssignmentIds.has(String(assignment.id))
      );
    });

    const studentExpected = expected
      .map(assignment =>
        contextByAssignment.get(String(assignment.id))
      )
      .filter(Boolean);

    const signatures = new Set(
      studentExpected.map(context => context.signature)
    );

    const allExpectedPresent =
      expected.length > 0 &&
      studentExpected.length === expected.length;

    const uniformSequence =
      allExpectedPresent &&
      signatures.size === 1;

    const thisSignature =
      sequenceContexts[0]?.signature || '';

    const thisGroupCoversAll =
      uniformSequence &&
      studentExpected.every(
        context => context.signature === thisSignature
      );

    if (thisGroupCoversAll) {
      const sequence = sequenceById.get(sequenceId);

      results.push(
        sequence?.name || 'Assignment Sequence'
      );

      sequenceContexts.forEach(context =>
        consumed.add(String(context.assignment.id))
      );

      continue;
    }

    const byType = new Map();

    sequenceContexts.forEach(context => {
      const typeId = String(
        context.assignment.assignment_type_id
      );

      if (!byType.has(typeId)) {
        byType.set(typeId, []);
      }

      byType.get(typeId).push(context);
    });

    for (const [typeId, typeContexts] of byType.entries()) {
      const setting = settingByType.get(typeId);
      const baseName = displayAssignmentName(
        typeContexts[0].assignment,
        setting
      );

      const allTypeContexts = allStudentContexts.filter(
        context =>
          String(context.assignment.assignment_type_id) === typeId
      );

      const typeSignatures = new Set(
        allTypeContexts.map(context => context.signature)
      );

      if (typeSignatures.size <= 1) {
        results.push(baseName);
      } else {
        typeContexts
          .slice()
          .sort(
            (a, b) =>
              Number(a.assignment.instance_number || 0) -
              Number(b.assignment.instance_number || 0)
          )
          .forEach(context => {
            results.push(
              resolveAssignmentInstanceLabel(
                context.assignment,
                {
                  baseName,
                  sameTypeCount:
                    allTypeContexts.length
                }
              )
            );
          });
      }

      typeContexts.forEach(context =>
        consumed.add(String(context.assignment.id))
      );
    }
  }

  const remaining = [
    ...nonSequence,
    ...contexts.filter(
      context =>
        !consumed.has(String(context.assignment.id)) &&
        context.sequenceId
    )
  ];

  const byType = new Map();

  remaining.forEach(context => {
    const typeId = String(
      context.assignment.assignment_type_id
    );

    if (!byType.has(typeId)) {
      byType.set(typeId, []);
    }

    byType.get(typeId).push(context);
  });

  for (const [typeId, typeContexts] of byType.entries()) {
    const setting = settingByType.get(typeId);
    const baseName = displayAssignmentName(
      typeContexts[0].assignment,
      setting
    );

    const allTypeContexts = allStudentContexts.filter(
      context =>
        String(context.assignment.assignment_type_id) === typeId
    );

    const typeSignatures = new Set(
      allTypeContexts.map(context => context.signature)
    );

    if (typeSignatures.size <= 1) {
      results.push(baseName);
    } else {
      typeContexts
        .slice()
        .sort(
          (a, b) =>
            Number(a.assignment.instance_number || 0) -
            Number(b.assignment.instance_number || 0)
        )
        .forEach(context => {
          results.push(
            resolveAssignmentInstanceLabel(
                context.assignment,
                {
                  baseName,
                  sameTypeCount:
                    allTypeContexts.length
                }
              )
          );
        });
    }
  }

  return Array.from(new Set(results.filter(Boolean)));
}

export default function StudentGroups({
  session,
  course,
  enrolment,
  hideHeading = false,
  hideIntro = false,
  hideEmpty = false,
  onGroupCountChange = null
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cards, setCards] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [saving, setSaving] = useState(false);

  const courseId = course?.id || null;
  const enrolmentId = enrolment?.id || null;

  useEffect(() => {
    if (
      !loading &&
      typeof onGroupCountChange ===
        'function'
    ) {
      onGroupCountChange(
        cards.length
      );
    }
  }, [
    loading,
    cards.length,
    onGroupCountChange
  ]);

  async function loadGroups() {
    if (!courseId || !enrolmentId) {
      setCards([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const [
        assignmentsResponse,
        settingsResponse,
        sequencesResponse,
        myMembershipsResponse,
        allMembershipsResponse,
        contextsResponse,
        logicalGroupsResponse,
        labels,
        descriptions
      ] = await Promise.all([
        fetch(
          `${SUPABASE_URL}/rest/v1/course_assignments?course_id=eq.${encodeURIComponent(courseId)}&select=id,course_id,assignment_type_id,instance_number,instance_name,groups_enabled,assignment_types(name)`,
          { headers: headers() }
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${encodeURIComponent(courseId)}&select=assignment_type_id,sequence_id,display_name`,
          { headers: headers() }
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/course_assignment_sequences?course_id=eq.${encodeURIComponent(courseId)}&select=id,name`,
          { headers: headers() }
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/assignment_group_memberships?enrolment_id=eq.${encodeURIComponent(enrolmentId)}&select=course_assignment_id,group_number`,
          { headers: headers() }
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/assignment_group_memberships?select=course_assignment_id,group_number,enrolment_id,enrolments(id,students(student_code,english_name,chinese_name,is_master)),course_assignments!inner(course_id)&course_assignments.course_id=eq.${encodeURIComponent(courseId)}`,
          { headers: headers() }
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/course_logical_group_contexts?select=logical_group_id,course_assignment_id,group_number`,
          { headers: headers() }
        ),
        fetch(
          `${SUPABASE_URL}/rest/v1/course_logical_groups?course_id=eq.${encodeURIComponent(courseId)}&select=id,course_id,name,description,membership_signature`,
          { headers: headers() }
        ),
        fetchAssignmentGroupLabelsForCourse(courseId),
        fetchAssignmentGroupDescriptionsForCourse(courseId)
      ]);

      const [
        assignments,
        settings,
        sequences,
        myMemberships,
        allMemberships,
        contextLinks,
        logicalGroups
      ] = await Promise.all([
        readJson(assignmentsResponse, 'Loading assignments', []),
        readJson(settingsResponse, 'Loading assignment settings', []),
        readJson(sequencesResponse, 'Loading assignment sequences', []),
        readJson(myMembershipsResponse, 'Loading your groups', []),
        readJson(allMembershipsResponse, 'Loading group members', []),
        readJson(contextsResponse, 'Loading logical group links', []),
        readJson(logicalGroupsResponse, 'Loading logical groups', [])
      ]);

      const assignmentById = new Map(
        (assignments || []).map(assignment => [
          String(assignment.id),
          assignment
        ])
      );

      const settingByType = new Map(
        (settings || []).map(setting => [
          String(setting.assignment_type_id),
          setting
        ])
      );

      const sequenceById = new Map(
        (sequences || []).map(sequence => [
          String(sequence.id),
          sequence
        ])
      );

      const logicalById = new Map(
        (logicalGroups || []).map(group => [
          String(group.id),
          group
        ])
      );

      const linkByPair = new Map(
        (contextLinks || []).map(link => [
          pairKey(link.course_assignment_id, link.group_number),
          link
        ])
      );

      const membershipsByPair = new Map();

      (allMemberships || []).forEach(row => {
        const key = pairKey(
          row.course_assignment_id,
          row.group_number
        );

        if (!membershipsByPair.has(key)) {
          membershipsByPair.set(key, []);
        }

        membershipsByPair.get(key).push(row);
      });

      const studentContexts =
        (myMemberships || [])
          .map(row => {
            const assignment =
              assignmentById.get(
                String(
                  row
                    .course_assignment_id
                )
              );

            if (!assignment) {
              return null;
            }

            const key =
              pairKey(
                row
                  .course_assignment_id,

                row
                  .group_number
              );

            const members =
              membershipsByPair.get(
                key
              ) || [];

            const signature =
              membershipSignature(
                members
              );

            const setting =
              settingByType.get(
                String(
                  assignment
                    .assignment_type_id
                )
              );

            const sequenceId =
              setting
                ?.sequence_id ||
              null;

            const link =
              linkByPair.get(
                key
              ) ||
              null;

            const logical =
              link
                ? logicalById.get(
                    String(
                      link
                        .logical_group_id
                    )
                  ) || null
                : null;

            const legacyName =
              labels?.[key] ||
              '';

            const legacyDescription =
              descriptions?.[key] ||
              '';

            return {
              source:
                'assignment',

              assignment,

              groupNumber:
                row.group_number,

              pairKey:
                key,

              members,
              signature,
              sequenceId,
              logical,

              logicalId:
                logical?.id ||
                null,

              customName:
                String(
                  logical?.name ||
                  legacyName ||
                  ''
                ).trim(),

              name:
                logical?.name ||
                legacyName ||
                `Group ${row.group_number}`,

              description:
                logical
                  ?.description ||
                legacyDescription ||
                ''
            };
          })
          .filter(Boolean);

      const courseGroupContext =
        await loadCourseGroupContext(
          courseId,
          enrolment
        );

      const allContexts = [
        ...studentContexts,

        ...(courseGroupContext
          ? [
              courseGroupContext
            ]
          : [])
      ];

      const contextGroups =
        groupContextsByIdentity(
          allContexts
        );

      const built =
        contextGroups
          .map(
            (
              groupContexts,
              index
            ) => {
              const assignmentContexts =
                groupContexts.filter(
                  context =>
                    context.source ===
                    'assignment'
                );

              const courseContexts =
                groupContexts.filter(
                  context =>
                    context.source ===
                    'course'
                );

              const logicalContexts =
                assignmentContexts
                  .filter(
                    context =>
                      context.logical
                  )
                  .slice()
                  .sort(
                    (a, b) =>
                      naturalCompare(
                        a.logicalId,
                        b.logicalId
                      )
                  );

              const logicalName =
                logicalContexts
                  .map(
                    context =>
                      String(
                        context
                          .logical
                          ?.name ||
                        ''
                      ).trim()
                  )
                  .find(Boolean) ||
                '';

              const logicalDescription =
                logicalContexts
                  .map(
                    context =>
                      String(
                        context
                          .logical
                          ?.description ||
                        ''
                      ).trim()
                  )
                  .find(Boolean) ||
                '';

              const courseCustomName =
                courseContexts
                  .map(
                    context =>
                      context
                        .customName
                  )
                  .find(Boolean) ||
                '';

              const assignmentCustomName =
                assignmentContexts
                  .map(
                    context =>
                      context
                        .customName
                  )
                  .find(Boolean) ||
                '';

              const name =
                logicalName ||
                courseCustomName ||
                assignmentCustomName ||
                courseContexts[0]
                  ?.name ||
                assignmentContexts[0]
                  ?.name ||
                'Group';

              const description =
                logicalDescription ||
                courseContexts
                  .map(
                    context =>
                      context
                        .description
                  )
                  .find(Boolean) ||
                assignmentContexts
                  .map(
                    context =>
                      context
                        .description
                  )
                  .find(Boolean) ||
                '';

              const memberByEnrolment =
                new Map();

              groupContexts.forEach(
                context => {
                  (
                    context.members ||
                    []
                  ).forEach(
                    row => {
                      const id =
                        String(
                          row
                            .enrolment_id ||
                          ''
                        );

                      if (
                        id &&
                        !memberByEnrolment
                          .has(id)
                      ) {
                        memberByEnrolment
                          .set(
                            id,
                            row
                          );
                      }
                    }
                  );
                }
              );

              const memberRows =
                Array.from(
                  memberByEnrolment
                    .values()
                )
                  .filter(
                    row =>
                      row
                        ?.enrolments
                        ?.students
                        ?.is_master !==
                      true
                  )
                  .sort(
                    (a, b) =>
                      naturalCompare(
                        a
                          ?.enrolments
                          ?.students
                          ?.student_code,

                        b
                          ?.enrolments
                          ?.students
                          ?.student_code
                      )
                  );

              const logicalIds =
                Array.from(
                  new Set(
                    assignmentContexts
                      .map(
                        context =>
                          context
                            .logicalId
                            ? String(
                                context
                                  .logicalId
                              )
                            : ''
                      )
                      .filter(Boolean)
                  )
                ).sort(
                  naturalCompare
                );

              const signatures =
                Array.from(
                  new Set(
                    groupContexts
                      .map(
                        context =>
                          String(
                            context
                              .signature ||
                            ''
                          )
                      )
                      .filter(Boolean)
                  )
                ).sort(
                  naturalCompare
                );

              const relevantTo =
                [];

              courseContexts.forEach(
                context => {
                  if (
                    context
                      .streamName
                  ) {
                    relevantTo.push(
                      context
                        .streamName
                    );
                  }
                }
              );

              if (
                assignmentContexts.length
              ) {
                relevantTo.push(
                  ...buildRelevantTo({
                    contexts:
                      assignmentContexts,

                    allStudentContexts:
                      studentContexts,

                    assignments:
                      assignments ||
                      [],

                    settingByType,
                    sequenceById
                  })
                );
              }

              return {
                key: [
                  'group',

                  ...logicalIds.map(
                    id =>
                      `logical:${id}`
                  ),

                  ...signatures.map(
                    signature =>
                      `members:${signature}`
                  ),

                  index
                ].join('|'),

                name,
                description,

                assignmentContexts,
                courseContexts,

                contexts:
                  assignmentContexts,

                members:
                  memberRows,

                relevantTo:
                  Array.from(
                    new Set(
                      relevantTo
                        .filter(Boolean)
                    )
                  )
              };
            }
          )
          .sort(
            (a, b) =>
              naturalCompare(
                a.name,
                b.name
              )
          );

      setCards(built);
    } catch (err) {
      console.error(
        'Loading Student Groups failed:',
        err
      );

      setError(
        err?.message ||
          'Could not load your Groups.'
      );
      setCards([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    courseId,
    enrolmentId,
    enrolment?.stream,
    enrolment?.group_number
  ]);

  const editable =
    session &&
    session.isMaster !== true;

  const cardByKey = useMemo(
    () =>
      new Map(
        cards.map(card => [
          card.key,
          card
        ])
      ),
    [cards]
  );

  function beginRename(card) {
    setEditingKey(card.key);
    setDraftName(card.name || '');
    setError('');
  }

  async function saveRename(event) {
    event.preventDefault();

    const card =
      cardByKey.get(
        editingKey
      );

    if (!card) {
      return;
    }

    const assignmentContexts =
      card.assignmentContexts ||
      card.contexts ||
      [];

    const courseContexts =
      card.courseContexts ||
      [];

    if (
      !assignmentContexts.length &&
      !courseContexts.length
    ) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      for (
        const courseContext
        of courseContexts
      ) {
        await renameStudentCourseGroup({
          session,

          courseId,

          streamNumber:
            courseContext
              .streamNumber,

          groupNumber:
            courseContext
              .groupNumber,

          name:
            draftName
        });
      }

      const seenLogicalIds =
        new Set();

      for (
        const target
        of assignmentContexts
      ) {
        if (
          target.logicalId
        ) {
          const logicalId =
            String(
              target.logicalId
            );

          if (
            seenLogicalIds.has(
              logicalId
            )
          ) {
            continue;
          }

          seenLogicalIds.add(
            logicalId
          );
        }

        await renameStudentAssignmentGroup({
          session,

          courseAssignmentId:
            target
              .assignment
              .id,

          groupNumber:
            target
              .groupNumber,

          name:
            draftName
        });
      }

      setEditingKey(
        null
      );

      await loadGroups();

    } catch (err) {
      setError(
        err?.message ||
          'The Group could not be renamed.'
      );

    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.muted}>
        Loading Groups...
      </div>
    );
  }

  if (
    hideEmpty &&
    !error &&
    !cards.length
  ) {
    return null;
  }

  return (
    <div style={styles.wrap}>
      {!hideHeading && (
        <h2 style={styles.heading}>
          Groups
        </h2>
      )}

      {!hideIntro && (
        <p style={styles.intro}>
          Your groups for this course are collected here. The same group is shown once wherever it is used.
        </p>
      )}

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {!cards.length ? (
        <div style={styles.empty}>
          You are not currently assigned to any groups in this course.
        </div>
      ) : (
        <div style={styles.list}>
          {cards.map(card => (
            <section
              key={card.key}
              style={styles.card}
            >
              <div style={styles.cardHeader}>
                <div>
                  <div style={styles.name}>
                    {card.name}
                  </div>

                  {card.description && (
                    <div style={styles.description}>
                      {card.description}
                    </div>
                  )}
                </div>

                {editable &&
                  editingKey !== card.key && (
                    <button
                      type="button"
                      style={styles.renameBtn}
                      onClick={() =>
                        beginRename(card)
                      }
                    >
                      Rename
                    </button>
                  )}
              </div>

              {editingKey === card.key && (
                <form
                  style={styles.renameForm}
                  onSubmit={saveRename}
                >
                  <input
                    autoFocus
                    maxLength={80}
                    value={draftName}
                    onChange={event =>
                      setDraftName(
                        event.target.value
                      )
                    }
                    style={styles.renameInput}
                  />

                  <button
                    disabled={saving}
                    style={styles.saveBtn}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>

                  <button
                    type="button"
                    disabled={saving}
                    style={styles.cancelBtn}
                    onClick={() =>
                      setEditingKey(null)
                    }
                  >
                    Cancel
                  </button>
                </form>
              )}

              <div style={styles.block}>
                <div style={styles.label}>
                  Members
                </div>

                <div style={styles.members}>
                  {card.members.map(member => {
                    const student =
                      member?.enrolments?.students || {};

                    return (
                      <div
                        key={member.enrolment_id}
                        style={styles.member}
                      >
                        <strong>
                          {studentName(member)}
                        </strong>

                        {student.student_code && (
                          <span>
                            {student.student_code}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={styles.block}>
                <div style={styles.label}>
                  Relevant to
                </div>

                <div style={styles.relevantList}>
                  {card.relevantTo.map(item => (
                    <span
                      key={item}
                      style={styles.relevantChip}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { width: '100%' },
  heading: {
    margin: '0 0 6px',
    color: '#1f2933',
    fontSize: '20px'
  },
  intro: {
    margin: '0 0 16px',
    color: '#66788a',
    fontSize: '13px',
    lineHeight: 1.45
  },
  muted: {
    color: '#66788a',
    fontSize: '14px'
  },
  error: {
    marginBottom: '12px',
    padding: '9px 11px',
    border: '1px solid #e8bcbc',
    borderRadius: '8px',
    background: '#faeaea',
    color: '#a12a2a',
    fontSize: '12px'
  },
  empty: {
    padding: '16px',
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    background: '#f7f9fa',
    color: '#66788a',
    fontSize: '13px'
  },
  list: {
    display: 'grid',
    gap: '12px'
  },
  card: {
    border: '1px solid #d7dee5',
    borderRadius: '12px',
    background: '#fff',
    overflow: 'hidden'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'flex-start',
    padding: '13px 14px',
    background: '#f7f9fa',
    borderBottom: '1px solid #e3e9ee'
  },
  name: {
    color: '#1f2933',
    fontSize: '15px',
    fontWeight: '800'
  },
  description: {
    marginTop: '3px',
    color: '#66788a',
    fontSize: '12px',
    lineHeight: 1.4
  },
  renameBtn: {
    flexShrink: 0,
    border: '1px solid #bcccdc',
    borderRadius: '6px',
    background: '#fff',
    color: '#42515f',
    padding: '5px 8px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  renameForm: {
    display: 'flex',
    gap: '7px',
    flexWrap: 'wrap',
    padding: '10px 14px',
    borderBottom: '1px solid #e3e9ee'
  },
  renameInput: {
    minWidth: 0,
    flex: '1 1 180px',
    border: '1px solid #bcccdc',
    borderRadius: '6px',
    padding: '7px 9px',
    fontSize: '13px'
  },
  saveBtn: {
    border: 0,
    borderRadius: '6px',
    background: '#216e45',
    color: '#fff',
    padding: '7px 10px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  cancelBtn: {
    border: '1px solid #bcccdc',
    borderRadius: '6px',
    background: '#fff',
    color: '#42515f',
    padding: '6px 10px',
    fontSize: '12px',
    cursor: 'pointer'
  },
  block: {
    padding: '12px 14px',
    borderBottom: '1px solid #edf1f4'
  },
  label: {
    marginBottom: '7px',
    color: '#66788a',
    fontSize: '10px',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  members: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
    gap: '6px'
  },
  member: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '7px 8px',
    border: '1px solid #e5eaee',
    borderRadius: '7px',
    background: '#fbfcfd',
    color: '#334e68',
    fontSize: '12px'
  },
  relevantList: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap'
  },
  relevantChip: {
    display: 'inline-block',
    padding: '4px 7px',
    borderRadius: '999px',
    background: '#e7f1f7',
    color: '#215b7d',
    fontSize: '11px',
    fontWeight: '700'
  }
};
